import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Helmet } from "react-helmet-async";

const IMAGE = "https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/9gutam0g_IMG-20260415-WA0052.jpg";

export default function FrozenChunksPage() {
  return (
    <main data-testid="frozen-chunks-page" className="pt-20">
      <Helmet>
        <title>Frozen Pineapple Chunks | Novapine | Bulk Supplier South Africa</title>
        <meta name="description" content="Buy frozen pineapple chunks in bulk from Novapine, Hluhluwe. Irregular-cut Cayenne pineapple chunks from Mkonge Farm. Perfect for smoothies, food service, bakeries, and retail. Reliable supply across South Africa." />
        <meta name="keywords" content="frozen pineapple chunks, pineapple chunks supplier South Africa, bulk frozen pineapple, frozen fruit chunks, Cayenne pineapple chunks, pineapple for smoothies, Novapine, Mkonge Farm, Hluhluwe" />
        <link rel="canonical" href="https://www.novapine.co.za/products/frozen-chunks" />
        <meta property="og:title" content="Frozen Pineapple Chunks | Novapine | Bulk Supplier South Africa" />
        <meta property="og:description" content="Buy frozen pineapple chunks in bulk. Irregular-cut Cayenne pineapple from Mkonge Farm. For smoothies, food service, and retail." />
        <meta property="og:image" content={IMAGE} />
      </Helmet>

      <section className="py-20 bg-novapine-brown">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-4">Product</p>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tighter text-white mb-4">Frozen Pineapple Chunks</h1>
          <p className="font-body text-base text-white/70 max-w-lg mx-auto">Irregular-cut frozen Cayenne pineapple chunks from Mkonge Farm, Hluhluwe.</p>
        </div>
      </section>

      <section className="py-24 bg-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img src={IMAGE} alt="Frozen pineapple chunks from Novapine" className="rounded-xl w-full h-[450px] object-cover" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-novapine-brown mb-6">Bulk Frozen Pineapple Chunks</h2>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed mb-6">
                Our frozen pineapple chunks are irregular-cut and frozen to maintain freshness and quality. Sourced from premium Cayenne pineapples grown on Mkonge Farm in the Hluhluwe region, KwaZulu-Natal.
              </p>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed mb-8">
                Available in bulk for commercial buyers, food service operators, and retailers. Whether you're making smoothies, desserts, or ready meals — our frozen chunks deliver consistent pineapple flavour year-round.
              </p>
              <h3 className="font-heading font-semibold text-sm uppercase tracking-[0.1em] text-novapine-gold mb-4">Applications</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
                {["Smoothie production", "Food service and catering", "Bakery and confectionery", "Dessert manufacturing", "Fruit salad production", "Retail frozen fruit packs"].map((app) => (
                  <div key={app} className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-novapine-gold shrink-0" />
                    <span className="font-body text-sm text-novapine-brown/70">{app}</span>
                  </div>
                ))}
              </div>
              <Link to="/contact">
                <Button className="btn-gold rounded-md px-8 py-5 font-heading text-sm tracking-wide font-semibold">
                  Enquire About Frozen Chunks <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-novapine-brown mb-6">Why Choose Novapine Frozen Pineapple Chunks?</h2>
          <div className="space-y-4">
            <p className="font-body text-base text-novapine-brown/70 leading-relaxed">
              <strong className="text-novapine-brown">Direct from Mkonge Farm.</strong> Every chunk is processed from Cayenne pineapples grown on our own farm in Hluhluwe — full traceability from field to freezer.
            </p>
            <p className="font-body text-base text-novapine-brown/70 leading-relaxed">
              <strong className="text-novapine-brown">Versatile and convenient.</strong> Perfect for smoothie bars, restaurants, bakeries, and home consumers looking for quality frozen pineapple.
            </p>
            <p className="font-body text-base text-novapine-brown/70 leading-relaxed">
              <strong className="text-novapine-brown">Year-round availability.</strong> Consistent supply backed by over 18 years of farming and processing experience.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-novapine-brown">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white mb-6">Ready to Order Frozen Pineapple Chunks?</h2>
          <p className="font-body text-base text-white/70 mb-10">Contact our team to discuss volumes, pricing, and delivery.</p>
          <Link to="/contact"><Button className="btn-gold rounded-md px-10 py-6 font-heading text-sm tracking-wide font-semibold">Get In Touch <ArrowRight size={16} className="ml-2" /></Button></Link>
        </div>
      </section>
    </main>
  );
}
